import { el, btn, trophySvg, championBadgeSvg } from '@/ui/dom';
import { buildWaitingBody } from '@/ui/onlineWaiting';
import { cssColor, fmtRaceTime, ordinal } from '@/ui/format';
import type { RaceFinishEntry } from '@/core/Game';
import type { ChampionshipRow, Trophy } from '@/core/Progression';
import { trophyForPlace, nextUnlockAt } from '@/core/Progression';
import { pointsForPlace } from '@/core/Progression';
import type { SkillAward } from '@/core/skillPoints';
import { animateCount } from '@/ui/SkillPointsToast';
import { SP_COIN_SVG, XP_STAR_SVG } from '@/ui/currencyIcons';
import { VEHICLE_ORDER } from '@/entities/vehicles';
import { REPORT_FLAG_SVG } from '@/ui/social/playerSheet';
import { panelHead, ridButton } from '@/ui/social/clubUi';
import { RaceCupStage } from '@/ui/celebration/RaceCup';
import { renderTourProgress, type TourProgressData } from '@/ui/screens/tourProgress';

/**
 * ResultsScreen.ts — the post-race panel (and, after the last tour city, the
 * end-of-tour championship summary). Owner reference art direction: podium cup
 * + "1ST PLACE" flag, standings rows with rank medallions, and a full GOLD
 * celebration (crest, gradient headline, gold button) for the world title.
 */
export interface ResultsContext {
  mode: 'quick' | 'tour';
  cityName: string;
  entries: RaceFinishEntry[];
  playerPlace: number;
  playerTimeMs: number | null;
  isNewBestTime: boolean;
  /** M62 — vehicle ids this race's points just unlocked (banner + confetti). */
  newlyUnlocked?: string[];
  /** M78 — the race's Skill-Point payout (breakdown card) + the player's
   *  career total AFTER it (the count-up target). Absent → no card. */
  award?: SkillAward;
  spTotal?: number;
  /** M102 — the green BANK (spendable) after the award; pairs with CAREER. */
  spBank?: number;
  /** M114 — the save's granted-forever vehicle set: the next-unlock progress
   *  bar must never advertise a legacy-granted ride as the "next" unlock. */
  grantedVehicles?: readonly string[];
  /** M100 — a GUEST's would-be payout: the points went to the guest pot, not
   *  the career. Renders the dimmed card + the CREATE RACER ID push. */
  guestPending?: { award: SkillAward; pot: number; potFull: boolean };
  /** The game's reduced-motion SETTING (CSS media queries can't see it). */
  reducedMotion?: boolean;
  /** 1-based race number within the tour (null for quick races). */
  tourRace: number | null;
  tourTotal: number;
  isLastTourRace: boolean;
  /** Online (M30): "Race Again" becomes "Rematch" and "Change City" → the hub. */
  online?: boolean;
  /** M114 — the one-time FIRST WIN module (offline, default difficulty only):
   *  offer the step up to HARD. `onStepUp` persists the new difficulty; the
   *  shown-once latch lives upstream in the save. */
  difficultyCta?: { onStepUp: () => void };
  /** M123 — the finished race was a free TEASER race for a non-owner: render
   *  the storefront card (gold GET THE FULL GAME CTA + the races-left line +
   *  a quiet "already own it?" sign-in link) in the sp-card-guest language.
   *  Flow only ever sets this on offline teaser races with the gate still
   *  armed — never for owners, never online, never on the demo race. */
  storefront?: {
    price: string;
    freeRacesLeft: number;
    onBuy: () => void;
    onSignIn: () => void;
  };
  /** M158c — an OFFLINE tour race's second step (the tour standings face). */
  tourStep?: TourProgressData;
  /** M158c — an ONLINE tour race: the standings step is the intermission
   *  screen (READY + countdown); this hands over to it. */
  onTourStandings?: () => void;
  /** M158c — online tour: when (Date.now() ms) the server starts the next city. */
  nextCityAt?: number;
  /** A re-view (back from the tour standings / the finale's stats toggle):
   *  numbers land instantly, no confetti replay, the cup still spins. */
  replay?: boolean;
}

export interface TourSummaryContext {
  table: ChampionshipRow[];
  trophies: Trophy[]; // the player's trophy per tour city, in order
  champion: boolean;
  /** M78 — the championship Skill-Point bonus (top-3 overall), revealed HERE
   *  (not on the last race's card — it belongs to the championship moment). */
  championshipBonus?: SkillAward | null;
  spTotal?: number;
  /** M102 — the green BANK (spendable) shown beside the career total. */
  spBank?: number;
  /** M114 — see ResultsContext.grantedVehicles. */
  grantedVehicles?: readonly string[];
  /** Vehicles the championship bonus's crossing unlocked. */
  newlyUnlocked?: string[];
  reducedMotion?: boolean;
  /** M100 — the guest's unclaimed tour payout (see ResultsContext). */
  guestPending?: { award: SkillAward; pot: number; potFull: boolean };
  /** M156 — an ONLINE tour: the standings carry REPORT A RACER. */
  online?: boolean;
}

export interface ResultsCallbacks {
  onNextCity: () => void; // tour: next city (or the summary after the last race)
  onRaceAgain: () => void;
  onChangeCity: () => void;
  onMenu: () => void;
  /** M100 — the guest card's CREATE RACER ID button. */
  onCreateAccount?: () => void;
  /** M156 — REPORT A RACER (online results + standings; App Store 1.2). */
  onReport?: () => void;
}

const CONFETTI_RACE = ['#ff3b3b', '#ffd23c', '#38e1ff', '#6ef0b9', '#b07cff', '#ff7ab8'];
const CONFETTI_GOLD = ['#ffd23c', '#ffe9a3', '#f4c542', '#fff3c4', '#c8891a', '#ffffff'];

/** Online tour: hand over to the standings this many seconds before the
 *  server starts the next city, so nobody misses the ranking or READY. */
const AUTO_STANDINGS_S = 6;
/** Online tour: how long the race card holds before it moves on by itself. */
const RESULTS_HOLD_S = 7;

const resultsVehicleLabel = (id: string): string =>
  VEHICLE_ORDER.find((v) => v.id === id)?.label ?? id;

export class ResultsScreen {
  private root: HTMLDivElement;
  private panel: HTMLDivElement;
  /** Live count-up cancellers (M78) — cleared on every repaint/hide. */
  private cancelFns: (() => void)[] = [];
  /** M94: the finale summary + its toggled final-race stats view. */
  private summaryCtx: TourSummaryContext | null = null;
  private summaryFinalRace: ResultsContext | null = null;
  /** M158c: the spinning 3D cup of the current face (stopped on repaint/hide). */
  private cup: RaceCupStage | null = null;
  private countLine: HTMLDivElement | null = null;

  constructor(
    parent: HTMLElement,
    private cb: ResultsCallbacks,
  ) {
    this.root = el('div', 'screen screen-results', parent);
    this.root.style.display = 'none';
    this.panel = el('div', 'panel results-panel', this.root);
  }

  /** M94: `detail` renders this as the tour finale's toggled "final race
   *  stats" view — no confetti, and the nav is a single back-to-standings.
   *  M158c (owner): rebuilt in the Racer ID dashboard's language — one
   *  gold-framed card on the HQ photo, a hero with the 3D race cup spinning
   *  (top 3) or a big place medallion, the rewards beside the standings, and
   *  the dashboard's tall glass buttons. A tour race then steps on to the TOUR
   *  STANDINGS (offline: inside this card; online: the intermission screen). */
  show(ctx: ResultsContext, detail?: { onBack: () => void }): void {
    this.clearAnimations();
    this.panel.replaceChildren();
    this.panel.className = 'rs-card';
    // Every result now wears the HQ photo (the dashboard family).
    this.root.classList.add('online-rank-bg');
    this.root.classList.toggle('reduced', !!ctx.reducedMotion);

    // Winner confetti (M14 polish) — over the whole screen on a win, and on any
    // race that unlocks a new vehicle (M62). Never on a toggled re-view.
    if (!detail && !ctx.replay && (ctx.playerPlace === 1 || (ctx.newlyUnlocked?.length ?? 0) > 0)) {
      this.burstConfetti(CONFETTI_RACE);
    }

    // ---- hero ----
    const cup = trophyForPlace(ctx.playerPlace);
    const hero = el('div', `rs-hero rs-hero-${cup}`, this.panel);
    const copy = el('div', 'rs-hero-copy', hero);
    el('div', 'rid-kicker', copy).textContent = ctx.online
      ? ctx.mode === 'tour'
        ? `ONLINE TOUR · RACE ${ctx.tourRace ?? 1} OF ${ctx.tourTotal}`
        : 'ONLINE RACE'
      : ctx.mode === 'tour'
        ? `WORLD TOUR · RACE ${ctx.tourRace ?? 1} OF ${ctx.tourTotal}`
        : 'QUICK RACE';
    const title = el('div', `rs-title${ctx.playerPlace === 1 ? ' rs-title-gold' : ''}`, copy);
    title.textContent =
      ctx.playerPlace === 1
        ? 'YOU WIN!'
        : ctx.playerPlace <= 3 && ctx.playerPlace > 0
          ? 'PODIUM FINISH!'
          : ctx.playerPlace > 0
            ? `${ordinal(ctx.playerPlace).toUpperCase()} PLACE`
            : 'RACE OVER';
    const chips = el('div', 'rs-chips', copy);
    const chip = (text: string, cls = ''): void => {
      el('span', `rs-chip${cls ? ` ${cls}` : ''}`, chips).textContent = text;
    };
    chip(ctx.cityName.toUpperCase());
    if (ctx.playerPlace > 0) {
      chip(`${ordinal(ctx.playerPlace).toUpperCase()} OF ${ctx.entries.length}`);
    }
    if (ctx.playerTimeMs !== null) chip(fmtRaceTime(ctx.playerTimeMs), 'rs-chip-time');
    if (ctx.isNewBestTime) chip('NEW BEST TIME', 'rs-chip-best');
    if (!detail && ctx.nextCityAt !== undefined) {
      this.countLine = el('div', 'rs-count', copy);
      this.startNextCityCount(ctx);
    }

    const stage = el('div', 'rs-hero-stage', hero);
    if (cup !== 'none') {
      el('div', 'rs-cup-glow', stage);
      this.cup = new RaceCupStage(stage, !!ctx.reducedMotion);
      this.cup.start(cup, ordinal(ctx.playerPlace).toUpperCase());
    } else if (ctx.playerPlace > 0) {
      this.addPlaceBadge(stage, ctx.playerPlace);
    }

    // ---- body: rewards (main) beside the race standings (side) ----
    const body = el('div', 'rs-body', this.panel);
    const main = el('div', 'rs-main', body);
    const side = el('div', 'rs-side', body);

    // M78: the SKILL POINTS payout card — breakdown rows, count-up career
    // total, progress bar to the next garage unlock. Every finished race earns
    // them — offline (M62) AND online (M63).
    if (ctx.award && ctx.award.total > 0 && ctx.spTotal !== undefined) {
      this.addSkillCard(
        main,
        ctx.award,
        ctx.spTotal,
        !!ctx.reducedMotion || !!ctx.replay,
        ctx.spBank,
        ctx.grantedVehicles,
      );
    } else if (ctx.guestPending && ctx.guestPending.award.total > 0) {
      // M100: guests see the payout they COULD have banked + the account push.
      this.addGuestCard(main, ctx.guestPending);
    }
    if (ctx.newlyUnlocked && ctx.newlyUnlocked.length > 0) {
      const unlock = el('div', 'results-unlock', main);
      unlock.textContent = `VEHICLE UNLOCKED: ${ctx.newlyUnlocked.map(resultsVehicleLabel).join(', ').toUpperCase()}`;
    }
    // M123: the teaser storefront card (same one-time-interactive rule as the
    // difficulty CTA — never on a toggled re-view, though a teaser race can
    // never be a finale anyway).
    if (!detail && ctx.storefront) this.addStorefrontCard(main, ctx.storefront);
    // M114: the one-time FIRST WIN difficulty offer (never on the finale's
    // toggled stats re-view — `detail` renders are a read-only replay).
    if (!detail && !ctx.replay && ctx.difficultyCta) this.addDifficultyCta(main, ctx.difficultyCta);
    if (main.childElementCount === 0) {
      main.remove();
      body.classList.add('rs-body-single');
    }

    // Standings rows (staggered entrance — winners first, M52).
    panelHead(side, 'RACE RESULTS', 'rs-standhead');
    const list = el('div', 'results-list rs-list', side);
    for (const [i, e] of ctx.entries.entries()) {
      const row = el('div', `results-row${e.isPlayer ? ' me' : ''}`, list);
      row.style.setProperty('--row-i', String(i));
      const medal = trophyForPlace(e.place);
      const rank = el('span', `results-rank${medal !== 'none' ? ` rs-rank-${medal}` : ''}`, row);
      rank.textContent = String(e.place);
      const dot = el('span', 'chip-dot', row);
      dot.style.background = cssColor(e.color);
      const name = el('span', 'results-name', row);
      name.textContent = e.name;
      const time = el('span', 'results-time', row);
      time.textContent = e.timeMs !== null ? fmtRaceTime(e.timeMs) : '—';
      if (ctx.mode === 'tour') {
        const pts = el('span', 'results-pts', row);
        pts.textContent = `+${pointsForPlace(e.place)}`;
      }
    }
    if (ctx.online) this.addReportLink(side);

    // ---- actions ----
    const actions = el('div', 'rs-actions', this.panel);
    if (detail) {
      ridButton(actions, { label: 'FINAL STANDINGS', variant: 'gold' }, detail.onBack);
      this.root.style.display = '';
      return;
    }
    const tourLeft = Math.max(0, ctx.tourTotal - (ctx.tourRace ?? 0));
    const standingsSub = `TOUR STANDINGS · ${tourLeft} ${tourLeft === 1 ? 'RACE' : 'RACES'} LEFT`;
    if (ctx.mode === 'tour' && ctx.onTourStandings) {
      // Online tour (M158c two-step): the standings + READY live on the
      // intermission screen. The card moves on BY ITSELF after a few seconds
      // (owner: online flows through the break between cities); CONTINUE
      // skips the wait, and a gold bar under it drains to show the timer.
      const go = ctx.onTourStandings;
      const cont = ridButton(
        actions,
        { label: 'CONTINUE', sub: standingsSub, variant: 'gold' },
        () => {
          this.clearAnimations();
          go();
        },
      );
      if (!ctx.replay) {
        const bar = el('span', 'rs-autobar', cont.el);
        bar.style.animationDuration = `${RESULTS_HOLD_S}s`;
      }
      btn('LEAVE TOUR', 'btn btn-danger-ghost rs-quiet', actions, () => this.cb.onMenu());
    } else if (ctx.mode === 'tour' && ctx.tourStep && !ctx.isLastTourRace) {
      const step = ctx.tourStep;
      ridButton(actions, { label: 'CONTINUE', sub: standingsSub, variant: 'gold' }, () =>
        this.showTourStep(ctx, step),
      );
      btn('Quit to Menu (progress saved)', 'btn btn-ghost rs-quiet', actions, () =>
        this.cb.onMenu(),
      );
    } else if (ctx.mode === 'tour') {
      ridButton(
        actions,
        { label: ctx.isLastTourRace ? 'TOUR RESULTS' : 'NEXT CITY', variant: 'gold' },
        () => this.cb.onNextCity(),
      );
      btn('Quit to Menu (progress saved)', 'btn btn-ghost rs-quiet', actions, () =>
        this.cb.onMenu(),
      );
    } else {
      const row = el('div', 'rs-actions-row', actions);
      ridButton(row, { label: ctx.online ? 'REMATCH' : 'RACE AGAIN', variant: 'gold' }, () =>
        this.cb.onRaceAgain(),
      );
      ridButton(row, { label: ctx.online ? 'ONLINE MENU' : 'CHANGE CITY', variant: 'glass' }, () =>
        this.cb.onChangeCity(),
      );
      btn('Menu', 'btn btn-ghost rs-quiet', actions, () => this.cb.onMenu());
    }

    this.root.style.display = '';
  }

  /** M158c — an offline tour race's SECOND step: the tour standings, in the
   *  same card. NEXT CITY flies the globe on; RACE RESULTS steps back. */
  private showTourStep(ctx: ResultsContext, step: TourProgressData): void {
    this.clearAnimations();
    this.panel.replaceChildren();
    this.panel.className = 'rs-card rs-tour';
    const body = el('div', 'rs-tourbody', this.panel);
    renderTourProgress(body, step);
    const actions = el('div', 'rs-actions', this.panel);
    const next = step.stops[step.raceNum]?.name;
    const row = el('div', 'rs-actions-row', actions);
    ridButton(
      row,
      {
        label: 'NEXT CITY',
        sub: next ? next.toUpperCase() : undefined,
        variant: 'gold',
      },
      () => this.cb.onNextCity(),
    );
    ridButton(row, { label: 'RACE RESULTS', variant: 'glass' }, () =>
      this.show({ ...ctx, replay: true, newlyUnlocked: [] }),
    );
    btn('Quit to Menu (progress saved)', 'btn btn-ghost rs-quiet', actions, () => this.cb.onMenu());
    this.root.style.display = '';
  }

  /** Online tour: the server starts the next city on its own clock, so the
   *  race card counts it down and hands over to the standings in good time. */
  private startNextCityCount(ctx: ResultsContext): void {
    const at = ctx.nextCityAt;
    const line = this.countLine;
    const go = ctx.onTourStandings;
    if (at === undefined || !line) return;
    let handed = false;
    const shownAt = Date.now();
    const tick = (): void => {
      const left = Math.max(0, Math.ceil((at - Date.now()) / 1000));
      line.textContent = left > 0 ? `NEXT CITY IN ${left}s` : 'NEXT CITY STARTING…';
      const held = Date.now() - shownAt >= RESULTS_HOLD_S * 1000;
      if (!handed && go && !ctx.replay && (held || left <= AUTO_STANDINGS_S)) {
        handed = true;
        window.setTimeout(() => {
          this.clearAnimations();
          go();
        }, 0);
      }
    };
    tick();
    const id = window.setInterval(tick, 500);
    this.cancelFns.push(() => window.clearInterval(id));
  }

  /** 4th and below: the place as a brushed steel badge, "5TH / PLACE". */
  private addPlaceBadge(stage: HTMLElement, place: number): void {
    const badge = el('div', 'rs-medal', stage);
    const num = el('div', 'rs-medal-num', badge);
    num.textContent = String(place);
    el('sup', 'rs-medal-suf', num).textContent = ordinal(place).replace(/^\d+/, '').toUpperCase();
    el('div', 'rs-medal-label', badge).textContent = 'PLACE';
  }

  /** M156 (App Store 1.2): the quiet REPORT A RACER link under online
   *  standings — every other racer's name can be reported or blocked. */
  private addReportLink(parent: HTMLElement): void {
    if (!this.cb.onReport) return;
    const link = el('button', 'mod-report-link results-report', parent);
    link.type = 'button';
    el('span', 'mod-link-ic', link).innerHTML = REPORT_FLAG_SVG;
    el('span', '', link).textContent = 'REPORT A RACER';
    link.addEventListener('click', () => this.cb.onReport?.());
  }

  /** The end-of-tour championship summary (shown after the last city; M94:
   *  after the podium celebration cinematic). `finalRace` = the last race's
   *  stats, reachable via the FINAL RACE STATS toggle instead of a screen of
   *  its own (owner spec). */
  showTourSummary(ctx: TourSummaryContext, finalRace?: ResultsContext): void {
    this.summaryCtx = ctx;
    this.summaryFinalRace = finalRace ?? null;
    this.renderSummary(true);
  }

  /** Render (or re-render, when toggling back from the race stats) the finale
   *  summary. `fresh` gates the one-time celebration effects — confetti and
   *  the skill-card count-ups don't replay on a toggle return. */
  private renderSummary(fresh: boolean): void {
    const ctx = this.summaryCtx;
    if (!ctx) return;
    this.clearAnimations();
    this.panel.replaceChildren();
    // M158c: the dashboard card on the HQ photo; the tour champion gets the
    // gold rim, a gold confetti rain and the 3D WORLD TOUR CUP in the hero.
    this.panel.className = `rs-card rs-summary${ctx.champion ? ' rs-champion' : ''}`;
    this.root.classList.add('online-rank-bg');
    if (ctx.champion && fresh) this.burstConfetti(CONFETTI_GOLD);

    const finalPlace = ctx.table.findIndex((r) => r.isPlayer) + 1;

    const hero = el(
      'div',
      `rs-hero rs-hero-${ctx.champion ? 'gold' : trophyForPlace(finalPlace)}`,
      this.panel,
    );
    const copy = el('div', 'rs-hero-copy', hero);
    el('div', 'rid-kicker', copy).textContent = 'WORLD TOUR CUP · FINAL STANDINGS';
    const title = el('div', `rs-title${ctx.champion ? ' rs-title-gold' : ''}`, copy);
    title.textContent = ctx.champion ? 'WORLD CHAMPION!' : 'TOUR COMPLETE';
    const sub = el('div', 'rs-sub', copy);
    // The trophy strip has one slot per tour city, so it tells us how long the
    // tour was (9 offline; 3 / 5 / 9 for an online tour).
    sub.textContent = ctx.champion
      ? `You conquered every street across ${ctx.trophies.length} cities.`
      : finalPlace <= 3
        ? 'A podium finish on the world stage.'
        : 'The title slipped away this time. Run it back.';
    const stage = el('div', 'rs-hero-stage', hero);
    const cupKind = trophyForPlace(finalPlace);
    if (ctx.champion) {
      el('div', 'rs-cup-glow', stage);
      this.cup = new RaceCupStage(stage, !!ctx.reducedMotion);
      const cupStage = this.cup;
      void cupStage.startTourCup().catch(() => {
        // the chunk failed to load: fall back to the championship crest
        if (this.cup === cupStage) stage.innerHTML = championBadgeSvg();
      });
    } else if (cupKind !== 'none') {
      el('div', 'rs-cup-glow', stage);
      this.cup = new RaceCupStage(stage, !!ctx.reducedMotion);
      this.cup.start(cupKind, ordinal(finalPlace).toUpperCase());
    } else if (finalPlace > 0) {
      this.addPlaceBadge(stage, finalPlace);
    }

    const cols = el('div', 'rs-body', this.panel);
    const main = el('div', 'rs-main', cols);
    const side = el('div', 'rs-side', cols);

    // M94: the PODIUM TRIO — the three winners as staggered medal cards (the
    // summary's echo of the celebration cinematic). Your card glows inside the
    // trio, your row golds in the table.
    this.addFinalePodium(main, ctx.table);

    // Championship table.
    panelHead(side, 'CHAMPIONSHIP STANDINGS', 'rs-standhead');
    const list = el('div', 'results-list rs-list', side);
    ctx.table.forEach((row, i) => {
      const r = el('div', `results-row${row.isPlayer ? ' me' : ''}`, list);
      r.style.setProperty('--row-i', String(i));
      const medal = trophyForPlace(i + 1);
      const rank = el('span', `results-rank${medal !== 'none' ? ` rs-rank-${medal}` : ''}`, r);
      rank.textContent = String(i + 1);
      const chip = el('span', 'chip-dot', r);
      chip.style.background = cssColor(row.color);
      const name = el('span', 'results-name', r);
      name.textContent = row.name;
      const wins = el('span', 'results-time results-wins', r);
      wins.textContent = `${row.wins} ${row.wins === 1 ? 'WIN' : 'WINS'}`;
      const pts = el('span', 'results-pts results-pts-big', r);
      pts.textContent = `${row.points} pts`;
    });
    if (ctx.online) this.addReportLink(side);

    // The player's trophy from each city, in tour order, + the tally line.
    const strip = el('div', 'results-trophystrip', side);
    for (const trophy of ctx.trophies) {
      const slot = el('span', 'results-trophyslot', strip);
      if (trophy !== 'none') slot.innerHTML = trophySvg(trophy);
    }
    const playerRow = ctx.table.find((r) => r.isPlayer);
    const wins = playerRow?.wins ?? 0;
    const tally = el('div', 'results-tally', side);
    tally.textContent =
      `${ctx.trophies.length} CITIES · ${wins} ${wins === 1 ? 'VICTORY' : 'VICTORIES'}` +
      (ctx.champion ? ' · 1 CHAMPION' : '');

    // M78: a top-3 championship pays its Skill-Point bonus HERE — the reveal
    // belongs to the championship moment, not the last race's card.
    if (ctx.championshipBonus && ctx.championshipBonus.total > 0 && ctx.spTotal !== undefined) {
      this.addSkillCard(
        main,
        ctx.championshipBonus,
        ctx.spTotal,
        !!ctx.reducedMotion || !fresh,
        ctx.spBank,
        ctx.grantedVehicles,
      );
    } else if (ctx.guestPending && ctx.guestPending.award.total > 0) {
      this.addGuestCard(main, ctx.guestPending); // M100: the guest's tour payout
    }
    if (ctx.newlyUnlocked && ctx.newlyUnlocked.length > 0) {
      const unlock = el('div', 'results-unlock', main);
      unlock.textContent = `VEHICLE UNLOCKED: ${ctx.newlyUnlocked.map(resultsVehicleLabel).join(', ').toUpperCase()}`;
    }

    const actions = el('div', 'rs-actions', this.panel);
    const row = el('div', 'rs-actions-row', actions);
    ridButton(row, { label: 'BACK TO MENU', variant: 'gold' }, () => this.cb.onMenu());
    // M94: the last race's stats live behind a toggle now — the celebration +
    // standings front the finale, the stats are there for whoever wants them.
    if (this.summaryFinalRace) {
      ridButton(row, { label: 'FINAL RACE STATS', variant: 'glass' }, () => {
        const race = this.summaryFinalRace;
        if (race) this.show({ ...race, replay: true }, { onBack: () => this.renderSummary(false) });
      });
    }

    this.root.style.display = '';
  }

  /** M94: the top-3 medal cards, podium-staggered (2nd · 1st · 3rd). */
  private addFinalePodium(parent: HTMLElement, table: ChampionshipRow[]): void {
    const top = table.slice(0, 3);
    if (top.length === 0) return;
    const wrap = el('div', 'finale-podium', parent);
    const order = [top[1], top[0], top[2]].filter((r): r is ChampionshipRow => r !== undefined);
    const placeWord = ['CHAMPION', '2ND PLACE', '3RD PLACE'];
    for (const row of order) {
      const place = table.indexOf(row) + 1;
      const cup = trophyForPlace(place);
      const card = el('div', `fp-card fp-${place}${row.isPlayer ? ' me' : ''}`, wrap);
      const glyph = el('div', 'fp-trophy', card);
      glyph.innerHTML = trophySvg(cup === 'none' ? 'gold' : cup);
      el('div', 'fp-place', card).textContent = placeWord[place - 1] ?? `${place}TH`;
      el('div', 'fp-name', card).textContent = row.name;
      el('div', 'fp-pts', card).textContent = `${row.points} pts`;
    }
  }

  /**
   * M78 — the race REWARDS payout card (renamed M131). Breakdown rows stagger
   * in, then the card splits into the two pools the ONE award feeds 1:1:
   * the gold XP panel (a total that rolls up from its pre-race value + the
   * progress bar toward the next garage unlock) and the green SP panel (the
   * spendable balance + where to spend it). `total` = the XP total AFTER
   * this award. Reduced motion lands everything instantly.
   */
  private addSkillCard(
    parent: HTMLElement,
    award: SkillAward,
    total: number,
    reduced: boolean,
    bank?: number,
    granted: readonly string[] = [],
  ): void {
    const card = el('div', 'sp-card', parent);
    const head = el('div', 'sp-card-head', card);
    const star = el('span', 'sp-card-star', head);
    star.innerHTML = XP_STAR_SVG;
    el('span', 'sp-card-title', head).textContent = 'RACE REWARDS';
    const gain = el('span', 'sp-card-gain', head);
    gain.textContent = `+${award.total} XP · +${award.total} SP`;

    const rows = el('div', 'sp-rows', card);
    let rowI = 0;
    const addRow = (label: string, amount: number, bonus: boolean): void => {
      if (amount <= 0) return;
      const row = el('div', `sp-row${bonus ? ' sp-row-bonus' : ''}`, rows);
      row.style.setProperty('--row-i', String(rowI++));
      el('span', 'sp-row-label', row).textContent = label;
      el('span', 'sp-row-amount', row).textContent = `+${amount}`;
    };
    addRow('RACE FINISH', award.base, false);
    addRow('PODIUM BONUS', award.podiumBonus, true);
    addRow('CHAMPIONSHIP BONUS', award.tourBonus, true);

    // M131 — the split: XP is PROGRESS (left, gold), SP is a BALANCE (right,
    // green). Same number lands in both; what happens next differs.
    const split = el('div', 'rw-split', card);
    const xpCol = el('div', 'rw-col rw-xp', split);
    const xpHead = el('div', 'rw-col-head', xpCol);
    const xpIc = el('span', 'rw-col-ic', xpHead);
    xpIc.innerHTML = XP_STAR_SVG;
    el('span', 'rw-col-label', xpHead).textContent = 'XP';
    const num = el('span', 'sp-total-num rw-col-num', xpCol);
    const before = Math.max(0, total - award.total);
    // Let the rows land first, then roll the total (the counter is the payoff).
    if (reduced) {
      num.textContent = String(total);
    } else {
      num.textContent = String(before);
      const t = window.setTimeout(() => {
        this.cancelFns.push(animateCount(num, before, total, 800, false));
        num.classList.add('sp-total-live');
      }, 450);
      this.cancelFns.push(() => window.clearTimeout(t));
    }

    // Progress toward the next garage unlock (or a full gold bar when done).
    const unlock = nextUnlockAt(total, granted);
    const track = el('div', 'sp-bar-track', xpCol);
    const fill = el('div', 'sp-bar-fill', track);
    const label = el('div', 'sp-bar-label', xpCol);
    if (unlock) {
      const threshold = total + unlock.pointsToGo;
      const fromPct = Math.min(100, Math.max(0, (before / threshold) * 100));
      const toPct = Math.min(100, (total / threshold) * 100);
      fill.style.width = `${(reduced ? toPct : fromPct).toFixed(1)}%`;
      if (!reduced) {
        const t = window.setTimeout(() => {
          fill.style.width = `${toPct.toFixed(1)}%`;
        }, 500);
        this.cancelFns.push(() => window.clearTimeout(t));
      }
      label.textContent = `NEXT RIDE: ${resultsVehicleLabel(unlock.id).toUpperCase()} · ${unlock.pointsToGo} XP TO GO`;
    } else {
      fill.style.width = '100%';
      label.textContent = 'ALL RIDES UNLOCKED';
    }

    if (bank !== undefined) {
      const spCol = el('div', 'rw-col rw-sp', split);
      const spHead = el('div', 'rw-col-head', spCol);
      const spIc = el('span', 'rw-col-ic', spHead);
      spIc.innerHTML = SP_COIN_SVG;
      el('span', 'rw-col-label', spHead).textContent = 'SP';
      const bankNum = el('span', 'sp-total-num rw-col-num sp-bank-num', spCol);
      const bankBefore = Math.max(0, bank - award.total);
      if (reduced) {
        bankNum.textContent = String(bank);
      } else {
        bankNum.textContent = String(bankBefore);
        const t = window.setTimeout(() => {
          this.cancelFns.push(animateCount(bankNum, bankBefore, bank, 800, false));
        }, 450);
        this.cancelFns.push(() => window.clearTimeout(t));
      }
      el('div', 'sp-bar-label rw-sp-note', spCol).textContent = 'SHOP POINTS · SPEND IN THE SHOP';
    }
  }

  /**
   * M100 — the GUEST payout card: the same breakdown the skill card shows,
   * dimmed, with the pot line + the gold CREATE RACER ID push. The points sit
   * in the guest pot (claimed in full the moment an account exists) — the
   * card is the owner's "motivate the account in the flow" moment.
   */
  private addGuestCard(
    parent: HTMLElement,
    pending: { award: SkillAward; pot: number; potFull: boolean },
  ): void {
    const card = el('div', 'sp-card sp-card-guest', parent);
    const head = el('div', 'sp-card-head', card);
    const star = el('span', 'sp-card-star', head);
    star.innerHTML = XP_STAR_SVG;
    el('span', 'sp-card-title', head).textContent = 'RACE REWARDS';
    el('span', 'sp-card-gain', head).textContent =
      `+${pending.award.total} XP · +${pending.award.total} SP`;

    const rows = el('div', 'sp-rows', card);
    let rowI = 0;
    const addRow = (label: string, amount: number): void => {
      if (amount <= 0) return;
      const row = el('div', 'sp-row', rows);
      row.style.setProperty('--row-i', String(rowI++));
      el('span', 'sp-row-label', row).textContent = label;
      el('span', 'sp-row-amount', row).textContent = `+${amount}`;
    };
    addRow('RACE FINISH', pending.award.base);
    addRow('PODIUM BONUS', pending.award.podiumBonus);
    addRow('CHAMPIONSHIP BONUS', pending.award.tourBonus);

    const totalRow = el('div', 'sp-totalrow', card);
    el('span', 'sp-total-label', totalRow).textContent = 'GUEST POT';
    el('span', 'sp-total-num', totalRow).textContent = `${pending.pot} XP & SP`;

    el('div', 'sp-guest-note', card).textContent = pending.potFull
      ? 'GUEST POT FULL — CREATE YOUR RACER ID TO KEEP EARNING'
      : 'WAITING FOR YOUR RACER ID — SIGN IN ONCE TO KEEP IT ALL';
    if (this.cb.onCreateAccount) {
      btn('CREATE RACER ID →', 'btn btn-gold sp-guest-btn', card, () => {
        this.cb.onCreateAccount?.();
      });
    }
  }

  /**
   * M123 — the teaser storefront card: rendered after a free TEASER race in
   * the guest-card visual language (sp-card-guest). Gold CTA → the paywall,
   * the races-left line keeps the soft cap honest, and the quiet sign-in link
   * covers "I already bought this on another device" (a restore, not a buy —
   * Flow wires it without purchase intent). M92 colour rule: gold affirmative,
   * nothing red.
   */
  private addStorefrontCard(
    parent: HTMLElement,
    store: NonNullable<ResultsContext['storefront']>,
  ): void {
    const card = el('div', 'sp-card sp-card-guest store-card', parent);
    const head = el('div', 'sp-card-head', card);
    const star = el('span', 'sp-card-star', head);
    star.innerHTML = XP_STAR_SVG;
    el('span', 'sp-card-title', head).textContent = 'FULL GAME';
    el('span', 'store-races', head).textContent =
      store.freeRacesLeft === 1 ? '1 FREE RACE LEFT' : `${store.freeRacesLeft} FREE RACES LEFT`;
    btn(`GET THE FULL GAME · ${store.price}`, 'btn btn-gold sp-guest-btn', card, store.onBuy);
    const signin = el('button', 'store-signin', card);
    signin.type = 'button';
    signin.textContent = 'Already own it? Sign in';
    signin.addEventListener('click', store.onSignIn);
  }

  /**
   * M114 — the one-time FIRST WIN module: the player just won their first
   * offline race on the default (medium) difficulty, so ask once whether to
   * step up. Gold = the affirmative step (M92 rule); KEEP MEDIUM is a quiet
   * ghost dismiss. Stepping up flips the module to a small confirmation; the
   * save-side latch upstream makes the whole module once-ever.
   */
  private addDifficultyCta(parent: HTMLElement, cta: { onStepUp: () => void }): void {
    const card = el('div', 'diffcta', parent);
    el('div', 'diffcta-kicker', card).textContent = 'FIRST WIN';
    el('div', 'diffcta-title', card).textContent = 'TOO EASY?';
    el('div', 'diffcta-sub', card).textContent = 'Step up the challenge or keep cruising.';
    const row = el('div', 'diffcta-row', card);
    btn('STEP UP TO HARD', 'btn btn-primary diffcta-btn', row, () => {
      cta.onStepUp();
      card.replaceChildren();
      el('div', 'diffcta-kicker', card).textContent = 'FIRST WIN';
      el('div', 'diffcta-confirm', card).textContent = 'DIFFICULTY SET TO HARD';
    });
    btn('KEEP MEDIUM', 'btn btn-ghost diffcta-btn', row, () => card.remove());
  }

  private clearAnimations(): void {
    for (const cancel of this.cancelFns) cancel();
    this.cancelFns = [];
    this.cup?.stop();
    this.cup = null;
    this.countLine = null;
  }

  /** A confetti rain over the whole screen (multicolour or gold). */
  private burstConfetti(colors: readonly string[]): void {
    const confetti = el('div', 'confetti', this.root);
    for (let i = 0; i < 44; i++) {
      const piece = el('span', '', confetti);
      piece.style.left = `${(i * 37) % 100}%`;
      piece.style.background = colors[i % colors.length];
      piece.style.animationDuration = `${2.2 + ((i * 13) % 17) / 10}s`;
      piece.style.animationDelay = `${((i * 7) % 12) / 10}s`;
    }
    window.setTimeout(() => confetti.remove(), 4500);
  }

  /** M75: the "waiting room" for an online QUICK race — shown when a finisher
   *  skips spectating (or finished with nobody left to watch). Replaced by
   *  show() the moment the server declares the race over. */
  showWaiting(cityName: string, onLeave: () => void): void {
    this.clearAnimations();
    this.panel.replaceChildren();
    this.panel.className = 'panel results-panel results-wait';
    // M115 (owner): the waiting room wears the menus' language now, and the
    // online standings share the LEADERBOARD backdrop (see .online-rank-bg).
    this.root.classList.add('online-rank-bg');
    buildWaitingBody(this.panel, cityName.toUpperCase(), 'FINISHED!', 'WAITING FOR THE OTHERS');
    const buttons = el('div', 'results-buttons', this.panel);
    btn('LEAVE RACE', 'btn btn-danger-ghost', buttons, onLeave);
    this.root.style.display = '';
  }

  /** M140: is the screen up? (the crew invite pop-up's "safe moment" check) */
  get isVisible(): boolean {
    return this.root.style.display !== 'none';
  }

  hide(): void {
    this.clearAnimations();
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.clearAnimations();
    this.root.remove();
  }
}
